import type { ReactNode } from 'react';
import { Card, CardContent, cn } from '@design-system';
import { carePhaseDefs } from '@shared';
import type { WalkthroughMonth } from '@shared/consumerPlan';
import { CARE_ENVIRONMENT_LABELS } from '../lib/copy';
import { money } from '../lib/format';
import { DetailList } from './figures';

const STATUS_COPY: Record<WalkthroughMonth['status'], string> = {
  beforeTrigger: 'Care hasn’t reached the policy’s benefit trigger yet.',
  waitingPeriod: 'The policy’s waiting period is in progress.',
  paying: 'The policy is paying benefits.',
  notPaying: 'No benefit payment is available this month.',
};

function momentLabel(moment: WalkthroughMonth) {
  return `Month ${moment.monthIndex + 1}, ${CARE_ENVIRONMENT_LABELS[moment.careEnvironment]}, ${carePhaseDefs[moment.carePhase].label}`;
}

function MomentStepper({
  moments,
  position,
  onSelect,
}: {
  moments: WalkthroughMonth[];
  position: number;
  onSelect: (index: number) => void;
}) {
  if (moments.length < 2) return null;

  return (
    <div className="flex items-center gap-1" role="tablist" aria-label="Moments in this care event">
      {moments.map((moment, index) => {
        const selected = index === position;
        return (
          <button
            key={moment.monthIndex}
            type="button"
            role="tab"
            aria-selected={selected}
            aria-label={momentLabel(moment)}
            className={cn(
              'h-2 w-2 shrink-0 rounded-full transition-all duration-300 ease-out',
              'focus-visible:ring-primary/40 focus-visible:ring-2 focus-visible:outline-none',
              selected
                ? 'bg-primary w-8'
                : index < position
                  ? 'bg-primary hover:w-3'
                  : 'bg-muted hover:w-3 hover:bg-gray-300',
            )}
            onClick={() => onSelect(index)}
          />
        );
      })}
    </div>
  );
}

function MomentBody({
  moment,
  aside,
  stepper,
}: {
  moment: WalkthroughMonth;
  aside?: ReactNode;
  stepper?: ReactNode;
}) {
  return (
    <>
      <div className="flex items-start justify-between gap-4">
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
            {stepper && <div className="w-full sm:order-last sm:w-auto">{stepper}</div>}
            <p className="min-w-0 text-base font-medium text-gray-950 sm:flex-1">
              <span className="text-primary">Month {moment.monthIndex + 1}</span>
              {' · '}
              {CARE_ENVIRONMENT_LABELS[moment.careEnvironment]} · {carePhaseDefs[moment.carePhase].label}
            </p>
          </div>
          <h2 className="text-xl font-semibold tracking-tight text-gray-950">{STATUS_COPY[moment.status]}</h2>
        </div>
        {aside}
      </div>
      <DetailList
        rows={[
          { label: 'Care bill this month', value: money(moment.careCost) },
          { label: 'Paid with insurance money', value: money(moment.paidByInsurance), tone: 'positive' },
          ...(moment.stillNeeded > 0.5 ? [{ label: 'Still needed for this month', value: money(moment.stillNeeded) }] : []),
        ]}
      />
    </>
  );
}

function MomentFrame({
  framed,
  children,
}: {
  framed: boolean;
  children: ReactNode;
}) {
  if (!framed) {
    return (
      <div data-testid="consumer-walkthrough-moment" className="flex flex-col gap-4 pt-4">
        {children}
      </div>
    );
  }

  return (
    <Card data-testid="consumer-walkthrough-moment">
      <CardContent className="flex flex-col gap-4">{children}</CardContent>
    </Card>
  );
}

export function WalkthroughMoment({
  moment,
  framed = true,
  aside,
  moments,
  position = 0,
  onSelectMoment,
}: {
  moment: WalkthroughMonth;
  framed?: boolean;
  aside?: ReactNode;
  moments?: WalkthroughMonth[];
  position?: number;
  onSelectMoment?: (index: number) => void;
}) {
  return (
    <MomentFrame framed={framed}>
      <MomentBody
        moment={moment}
        aside={aside}
        stepper={
          moments && onSelectMoment ? (
            <MomentStepper moments={moments} position={position} onSelect={onSelectMoment} />
          ) : undefined
        }
      />
    </MomentFrame>
  );
}
